import {
    ApiResponse,
    PaginatedResponse,
    UserInfo,
    Job,
    Application,
    FileTransfer,
    Dataset,
    Image,
    BillRecord,
    RechargeRecord,
    SSHNode,
    SystemStats,
    JobListParams,
    BillListParams,
    CreateJobParams,
    RechargeParams,
    PaginationParams,
    PricingParams,
    PricingResponse,
    Ticket,
    CreateTicketParams,
    UpdateTicketParams,
    TicketComment,
    Voucher,
    VoucherListParams,
    CreateVoucherParams,
    User,
    SubUser,
    UserListParams,
    CreateUserParams,
    UpdateUserParams,
    JobStatusResponse,
    CancelJobResponse,
    ClusterConfig,
    ConfigUpdateResponse,
    BillingSummary,
    BillingRecord,
    BillingListParams,
    JobCostDetail,
    ResourcePricing,
    SubmitJobParams,
    SubmitJobResponse,
    AIProject,
    AIProjectTemplate,
    CreateAIProjectParams,
    UpdateAIProjectParams,
    AIProjectListParams,
    AIProjectAction,
    AIProjectActionResponse
} from '../types/api';

import {
    mockJobs,
    mockBillRecords,
    mockJobCostDetails,
    mockClusterConfig,
    mockDatasets,
    mockImages,
    mockKeyPairs,
    mockQuotas,
    mockUser,
    mockVouchers,
    mockTickets,
    mockResourcePricing,
    mockAIProjects,
    mockAIProjectTemplates,
    mockApplications,
    mockFileTransfers,
    mockRechargeRecords,
    mockSSHNodes,
    mockSystemStats,
    mockComputePrices,
    mockStorageSystems,
    mockAdmins,
    mockSubUsers,
    mockBillingSummary
} from '../mock/data';

// 环境配置
const isDevelopment = import.meta.env.DEV;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.chaincloud.com';

// 模拟网络延迟
const mockDelay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// HTTP客户端封装
class ApiClient {
    private baseURL: string;

    constructor(baseURL: string) {
        this.baseURL = baseURL;
    }

    async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`;

        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    async get<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }
}

const apiClient = new ApiClient(API_BASE_URL);

// API服务类
export class ApiService {
    // 用户相关API
    static async getUserInfo(): Promise<ApiResponse<UserInfo>> {
        if (isDevelopment) {
            await mockDelay();
            return {
                success: true,
                data: mockUser,
                message: 'Success'
            };
        }
        return apiClient.get<UserInfo>('/user/info');
    }

    static async updateUserInfo(data: Partial<UserInfo>): Promise<ApiResponse<UserInfo>> {
        if (isDevelopment) {
            await mockDelay();
            return {
                success: true,
                data: { ...mockUser, ...data },
                message: 'User updated successfully'
            };
        }
        return apiClient.put<UserInfo>('/user/info', data);
    }

    // 作业相关API
    static async getJobs(params?: JobListParams): Promise<ApiResponse<PaginatedResponse<Job>>> {
        if (isDevelopment) {
            await mockDelay();
            const { page = 1, pageSize = 10, status, application } = params || {};

            let filteredJobs = mockJobs;
            if (status) {
                filteredJobs = filteredJobs.filter(job => job.status === status);
            }
            if (application) {
                filteredJobs = filteredJobs.filter(job => job.application === application);
            }

            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const items = filteredJobs.slice(startIndex, endIndex);

            return {
                success: true,
                data: {
                    items,
                    total: filteredJobs.length,
                    page,
                    pageSize,
                    totalPages: Math.ceil(filteredJobs.length / pageSize)
                }
            };
        }

        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params?.status) queryParams.append('status', params.status);
        if (params?.application) queryParams.append('application', params.application);

        return apiClient.get<PaginatedResponse<Job>>(`/jobs?${queryParams}`);
    }

    static async createJob(data: CreateJobParams): Promise<ApiResponse<Job>> {
        if (isDevelopment) {
            await mockDelay();
            const newJob: Job = {
                id: `job_${Date.now()}`,
                name: data.name,
                status: 'pending',
                user: 'current_user',
                submitTime: new Date().toISOString(),
                nodes: 1,
                cores: data.cpuCores || 1,
                app: data.application,
                application: data.application,
                masterId: 'master1',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                cost: 0
            };
            return {
                success: true,
                data: newJob,
                message: 'Job created successfully'
            };
        }
        return apiClient.post<Job>('/jobs', data);
    }

    static async cancelJobSimple(jobId: string): Promise<ApiResponse<void>> {
        if (isDevelopment) {
            await mockDelay();
            return {
                success: true,
                data: undefined,
                message: 'Job cancelled successfully'
            };
        }
        return apiClient.delete<void>(`/jobs/${jobId}`);
    }

    // 应用中心API
    static async getApplications(params: PaginationParams): Promise<PaginatedResponse<Application>> {
        await mockDelay();
        const { page = 1, pageSize = 10 } = params;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        const items = mockApplications.slice(startIndex, endIndex);

        return {
            items,
            total: mockApplications.length,
            page,
            pageSize,
            totalPages: Math.ceil(mockApplications.length / pageSize)
        };
    }

    static async installApplication(appId: string): Promise<ApiResponse<void>> {
        if (isDevelopment) {
            await mockDelay();
            return {
                success: true,
                data: undefined,
                message: 'Application installed successfully'
            };
        }
        return apiClient.post<void>(`/applications/${appId}/install`);
    }

    // 文件传输API
    static async getFileTransfers(): Promise<ApiResponse<FileTransfer[]>> {
        await mockDelay();
        return {
            success: true,
            data: mockFileTransfers
        };
    }

    static async uploadFile(file: File): Promise<ApiResponse<FileTransfer>> {
        if (isDevelopment) {
            await mockDelay();
            const newTransfer: FileTransfer = {
                id: `transfer_${Date.now()}`,
                fileName: file.name,
                fileSize: file.size,
                type: 'upload',
                status: 'pending',
                progress: 0,
                speed: 0,
                createdAt: new Date().toISOString()
            };
            return {
                success: true,
                data: newTransfer,
                message: 'File upload started'
            };
        }

        const formData = new FormData();
        formData.append('file', file);

        return apiClient.request<FileTransfer>('/files/upload', {
            method: 'POST',
            body: formData,
            headers: {} // 让浏览器自动设置Content-Type
        });
    }

    // 数据集API
    static async getDatasets(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Dataset>>> {
        if (isDevelopment) {
            await mockDelay();
            const { page = 1, pageSize = 10 } = params || {};
            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const items = mockDatasets.slice(startIndex, endIndex);

            return {
                success: true,
                data: {
                    items,
                    total: mockDatasets.length,
                    page,
                    pageSize,
                    totalPages: Math.ceil(mockDatasets.length / pageSize)
                }
            };
        }

        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

        return apiClient.get<PaginatedResponse<Dataset>>(`/datasets?${queryParams}`);
    }

    // 镜像中心API
    static async getImages(params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Image>>> {
        if (isDevelopment) {
            await mockDelay();
            const { page = 1, pageSize = 10 } = params || {};
            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const items = mockImages.slice(startIndex, endIndex);

            return {
                success: true,
                data: {
                    items,
                    total: mockImages.length,
                    page,
                    pageSize,
                    totalPages: Math.ceil(mockImages.length / pageSize)
                }
            };
        }

        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

        return apiClient.get<PaginatedResponse<Image>>(`/images?${queryParams}`);
    }

    // 账单相关API
    static async getBillRecords(params?: BillListParams): Promise<ApiResponse<PaginatedResponse<BillRecord>>> {
        if (isDevelopment) {
            await mockDelay();
            const { page = 1, pageSize = 10, period, productType } = params || {};

            let filteredRecords = mockBillRecords;
            if (period) {
                filteredRecords = filteredRecords.filter(record => record.period === period);
            }
            if (productType) {
                filteredRecords = filteredRecords.filter(record => record.productType === productType);
            }

            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const items = filteredRecords.slice(startIndex, endIndex);

            return {
                success: true,
                data: {
                    items,
                    total: filteredRecords.length,
                    page,
                    pageSize,
                    totalPages: Math.ceil(filteredRecords.length / pageSize)
                }
            };
        }

        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params?.period) queryParams.append('period', params.period);
        if (params?.productType) queryParams.append('productType', params.productType);

        return apiClient.get<PaginatedResponse<BillRecord>>(`/billing/records?${queryParams}`);
    }

    static async recharge(data: RechargeParams): Promise<ApiResponse<RechargeRecord>> {
        if (isDevelopment) {
            await mockDelay();
            const newRecharge: RechargeRecord = {
                id: `recharge_${Date.now()}`,
                ...data,
                status: 'pending',
                createdAt: new Date().toISOString(),
                transactionId: `${data.paymentMethod}_${Date.now()}`
            };
            return {
                success: true,
                data: newRecharge,
                message: 'Recharge initiated successfully'
            };
        }
        return apiClient.post<RechargeRecord>('/billing/recharge', data);
    }

    static async getRechargeRecords(): Promise<ApiResponse<RechargeRecord[]>> {
        await mockDelay();
        return {
            success: true,
            data: mockRechargeRecords
        };
    }

    // SSH节点API
    static async getSSHNodes(): Promise<ApiResponse<SSHNode[]>> {
        await mockDelay();
        return {
            success: true,
            data: mockSSHNodes
        };
    }

    static async createSSHNode(): Promise<ApiResponse<SSHNode>> {
        if (isDevelopment) {
            await mockDelay();
            const newNode: SSHNode = {
                id: `ssh_${Date.now()}`,
                name: `compute-node-${Date.now()}`,
                host: `node${Date.now()}.chaincloud.com`,
                port: 22,
                status: 'starting',
                cpuUsage: 0,
                memoryUsage: 0,
                createdAt: new Date().toISOString()
            };
            return {
                success: true,
                data: newNode,
                message: 'SSH node created successfully'
            };
        }
        return apiClient.post<SSHNode>('/ssh/nodes');
    }

    // 系统统计API
    static async getSystemStats(): Promise<ApiResponse<SystemStats>> {
        await mockDelay();
        return {
            success: true,
            data: mockSystemStats
        };
    }

    // 价格标准API
    static async getPricing(params: PricingParams): Promise<PricingResponse> {
        await mockDelay();
        const { region, type } = params;

        let computePrices = [...mockComputePrices];

        if (region) {
            computePrices = computePrices.filter(price => price.region === region);
        }

        if (type === 'gpu') {
            computePrices = computePrices.filter(price => price.gpus !== '—');
        } else if (type === 'compute') {
            computePrices = computePrices.filter(price => price.gpus === '—');
        }

        return {
            computePrices,
            storageSystems: type === 'storage' ? mockStorageSystems : undefined
        };
    }

    // 工单管理API
    static async getTickets(params: PaginationParams): Promise<PaginatedResponse<Ticket>> {
        await mockDelay();
        const { page = 1, pageSize = 10 } = params;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        const items = mockTickets.slice(startIndex, endIndex);

        return {
            items,
            total: mockTickets.length,
            page,
            pageSize,
            totalPages: Math.ceil(mockTickets.length / pageSize)
        };
    }

    static async getTicket(ticketId: string): Promise<Ticket> {
        await mockDelay();
        const ticket = mockTickets.find(t => t.id === ticketId);
        if (!ticket) {
            throw new Error('Ticket not found');
        }
        return ticket;
    }

    static async createTicket(data: CreateTicketParams): Promise<Ticket> {
        await mockDelay();
        const newTicket: Ticket = {
            id: `TK-${Date.now()}`,
            username: mockUser.username,
            subject: data.subject,
            description: data.description,
            status: 'pending',
            handler: '待分配',
            createdAt: new Date().toISOString(),
            priority: data.priority,
            category: data.category,
            attachments: data.attachments,
            comments: []
        };
        mockTickets.push(newTicket);
        return newTicket;
    }

    // 代金券管理API
    static async getVouchers(params?: VoucherListParams): Promise<ApiResponse<PaginatedResponse<Voucher>>> {
        if (isDevelopment) {
            await mockDelay();
            const { page = 1, pageSize = 10, status, type, validOnly } = params || {};

            let filteredVouchers = mockVouchers;
            if (status) {
                filteredVouchers = filteredVouchers.filter(v => v.status === status);
            }
            if (type) {
                filteredVouchers = filteredVouchers.filter(v => v.type === type);
            }
            if (validOnly) {
                const now = new Date().toISOString();
                filteredVouchers = filteredVouchers.filter(v =>
                    v.validFrom <= now && v.validTo >= now && v.status === 'unused'
                );
            }

            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const items = filteredVouchers.slice(startIndex, endIndex);

            return {
                success: true,
                data: {
                    items,
                    total: filteredVouchers.length,
                    page,
                    pageSize,
                    totalPages: Math.ceil(filteredVouchers.length / pageSize)
                }
            };
        }
        return apiClient.get<PaginatedResponse<Voucher>>('/vouchers');
    }

    static async createVoucher(data: CreateVoucherParams): Promise<ApiResponse<Voucher>> {
        if (isDevelopment) {
            await mockDelay();
            const newVoucher: Voucher = {
                id: `V-${Date.now()}`,
                code: data.code,
                amount: data.amount,
                type: data.type,
                status: 'unused',
                validFrom: data.validFrom,
                validTo: data.validUntil,
                description: data.description,
                restrictions: data.restrictions,
                minSpend: data.minSpend,
                maxDiscount: data.maxDiscount,
                applicableProducts: data.applicableProducts
            };
            return {
                success: true,
                data: newVoucher,
                message: '代金券创建成功'
            };
        }
        return apiClient.post<Voucher>('/vouchers', data);
    }

    static async useVoucher(voucherId: string): Promise<ApiResponse<Voucher>> {
        if (isDevelopment) {
            await mockDelay();
            const voucher = mockVouchers.find(v => v.id === voucherId);
            if (!voucher) {
                throw new Error('代金券不存在');
            }
            if (voucher.status !== 'unused') {
                throw new Error('代金券已使用或已过期');
            }
            const updatedVoucher: Voucher = {
                ...voucher,
                status: 'used' as const,
                usedAt: new Date().toISOString(),
                usedBy: mockUser.username
            };
            return {
                success: true,
                data: updatedVoucher,
                message: '代金券使用成功'
            };
        }
        return apiClient.post<Voucher>(`/vouchers/${voucherId}/use`);
    }

    // 用户管理API
    static async getUsers(params: UserListParams): Promise<PaginatedResponse<User | SubUser>> {
        await mockDelay();
        const { type, role, status, search, page = 1, pageSize = 10 } = params;

        const users = type === 'admin' ? [...mockAdmins] : [...mockSubUsers];

        let filteredUsers: any[] = users;
        if (role) {
            filteredUsers = filteredUsers.filter(user => user.role === role);
        }
        if (status && type === 'admin') {
            filteredUsers = filteredUsers.filter(user => user.status === status);
        }
        if (search) {
            const searchLower = search.toLowerCase();
            filteredUsers = filteredUsers.filter(user =>
                user.username.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower)
            );
        }

        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const items = filteredUsers.slice(startIndex, endIndex);

        return {
            items,
            total: filteredUsers.length,
            page,
            pageSize,
            totalPages: Math.ceil(filteredUsers.length / pageSize)
        };
    }

    static async createUser(data: CreateUserParams): Promise<ApiResponse<User | SubUser>> {
        if (isDevelopment) {
            await mockDelay();
            const isAdmin = [ 'org_admin', 'account_admin', 'system_admin', 'security_admin' ].includes(data.role);

            if (isAdmin) {
                const newAdmin: User = {
                    id: `admin_${Date.now()}`,
                    username: data.username,
                    email: data.email,
                    phone: data.phone || '',
                    role: data.role as User[ 'role' ],
                    status: 'active',
                    createdAt: new Date().toISOString().split('T')[ 0 ]
                };
                return {
                    success: true,
                    data: newAdmin,
                    message: '管理员创建成功'
                };
            } else {
                const newSubUser: SubUser = {
                    id: `user_${Date.now()}`,
                    username: data.username,
                    email: data.email,
                    role: data.role as SubUser[ 'role' ],
                    monthlyQuota: data.monthlyQuota || 0,
                    yearlyQuota: data.yearlyQuota || 0,
                    usedQuota: 0,
                    createdAt: new Date().toISOString().split('T')[ 0 ]
                };
                return {
                    success: true,
                    data: newSubUser,
                    message: '子用户创建成功'
                };
            }
        }
        return apiClient.post<User | SubUser>('/users', data);
    }

    static async updateUser(userId: string, data: UpdateUserParams): Promise<ApiResponse<User | SubUser>> {
        if (isDevelopment) {
            await mockDelay();
            const admin = mockAdmins.find(a => a.id === userId);
            if (admin) {
                const updatedAdmin: User = {
                    ...admin,
                    ...(data as Partial<User>)
                };
                return {
                    success: true,
                    data: updatedAdmin,
                    message: '管理员信息更新成功'
                };
            }

            const subUser = mockSubUsers.find(u => u.id === userId);
            if (subUser) {
                const updatedSubUser: SubUser = {
                    ...subUser,
                    ...(data as Partial<SubUser>)
                };
                return {
                    success: true,
                    data: updatedSubUser,
                    message: '子用户信息更新成功'
                };
            }

            throw new Error('用户不存在');
        }
        return apiClient.put<User | SubUser>(`/users/${userId}`, data);
    }

    static async deleteUser(userId: string, type: 'admin' | 'subuser'): Promise<void> {
        await mockDelay();

        if (type === 'admin') {
            const adminIndex = mockAdmins.findIndex(a => a.id === userId);
            if (adminIndex === -1) {
                throw new Error('Admin not found');
            }
            mockAdmins.splice(adminIndex, 1);
        } else {
            const subUserIndex = mockSubUsers.findIndex(u => u.id === userId);
            if (subUserIndex === -1) {
                throw new Error('SubUser not found');
            }
            mockSubUsers.splice(subUserIndex, 1);
        }
    }

    // 作业管理API
    static async getJobStatus(masterId: string, jobId: string): Promise<JobStatusResponse> {
        await mockDelay();
        const job = mockJobs.find(j => j.id === jobId);
        if (!job) {
            throw new Error('作业不存在');
        }
        return {
            errorCode: "0",
            status: "DONE",
            message: "Success",
            jobStatus: job.status
        };
    }

    static async cancelJob(masterId: string, jobId: string, requestId?: string): Promise<CancelJobResponse> {
        await mockDelay();
        const job = mockJobs.find(j => j.id === jobId);
        if (!job) {
            throw new Error('Job not found');
        }
        job.status = 'cancelled';
        return {
            success: true,
            message: 'Job cancelled successfully'
        };
    }

    // 集群配置API
    static async getClusterConfig(masterId: string): Promise<ClusterConfig> {
        await mockDelay();
        return mockClusterConfig;
    }

    static async updateClusterConfig(config: ClusterConfig): Promise<ConfigUpdateResponse> {
        await mockDelay();
        Object.assign(mockClusterConfig, config);
        return {
            errorCode: "0",
            message: "Success"
        };
    }

    // 计费中心API
    static async getBillingSummary(): Promise<BillingSummary> {
        await mockDelay();
        return mockBillingSummary;
    }

    static async getBillingRecords(params: BillingListParams): Promise<{
        records: BillRecord[];
        total: number;
    }> {
        await mockDelay();
        let records = [...mockBillRecords];

        // 应用过滤条件
        if (params.type) {
            records = records.filter(record => record.type === params.type);
        }

        // 应用日期范围过滤
        if (params.startDate) {
            const startDate = new Date(params.startDate);
            records = records.filter(record => new Date(record.date) >= startDate);
        }

        if (params.endDate) {
            const endDate = new Date(params.endDate);
            records = records.filter(record => new Date(record.date) <= endDate);
        }

        // 应用搜索过滤
        if (params.search) {
            const searchLower = params.search.toLowerCase();
            records = records.filter(record =>
                record.description.toLowerCase().includes(searchLower)
            );
        }

        // 应用分页
        const start = (params.page - 1) * params.pageSize;
        const end = start + params.pageSize;
        const paginatedRecords = records.slice(start, end);

        return {
            records: paginatedRecords,
            total: records.length
        };
    }

    static async getJobCostDetails(jobIds: string[]): Promise<JobCostDetail[]> {
        await mockDelay();
        return mockJobCostDetails.filter(detail => jobIds.includes(detail.jobId));
    }

    static async getResourcePricing(): Promise<ResourcePricing[]> {
        await mockDelay();
        return mockResourcePricing;
    }

    static async submitJob(params: SubmitJobParams): Promise<SubmitJobResponse> {
        await mockDelay();

        const newJob: Job = {
            id: `job_${Date.now()}`,
            name: params.name,
            status: 'pending',
            user: 'current_user',
            submitTime: new Date().toISOString(),
            nodes: 1,
            cores: params.cpuCores || 1,
            app: params.application,
            application: params.application,
            masterId: 'master1',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            cost: 0
        };

        mockJobs.push(newJob);

        return {
            success: true,
            jobId: newJob.id,
            message: '作业提交成功'
        };
    }

    // AI项目相关API
    static async getAIProjects(params: AIProjectListParams = {}): Promise<ApiResponse<PaginatedResponse<AIProject>>> {
        if (isDevelopment) {
            await mockDelay();
            const { page = 1, pageSize = 10, status, type, framework, search, sortBy = 'updatedAt', sortOrder = 'desc' } = params;
            
            let filteredProjects = [...mockAIProjects];
            
            // 过滤
            if (status) {
                filteredProjects = filteredProjects.filter(p => p.status === status);
            }
            if (type) {
                filteredProjects = filteredProjects.filter(p => p.type === type);
            }
            if (framework) {
                filteredProjects = filteredProjects.filter(p => p.framework === framework);
            }
            if (search) {
                const searchLower = search.toLowerCase();
                filteredProjects = filteredProjects.filter(p => 
                    p.name.toLowerCase().includes(searchLower) ||
                    p.description?.toLowerCase().includes(searchLower)
                );
            }
            
            // 排序
            filteredProjects.sort((a, b) => {
                const aVal = a[sortBy as keyof AIProject];
                const bVal = b[sortBy as keyof AIProject];
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    if (sortOrder === 'asc') {
                        return aVal.localeCompare(bVal);
                    } else {
                        return bVal.localeCompare(aVal);
                    }
                }
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    if (sortOrder === 'asc') {
                        return aVal - bVal;
                    } else {
                        return bVal - aVal;
                    }
                }
                return 0;
            });
            
            const startIndex = (page - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const items = filteredProjects.slice(startIndex, endIndex);

            return {
                success: true,
                data: {
                    items,
                    total: filteredProjects.length,
                    page,
                    pageSize,
                    totalPages: Math.ceil(filteredProjects.length / pageSize)
                }
            };
        }

        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params?.status) queryParams.append('status', params.status);
        if (params?.type) queryParams.append('type', params.type);
        if (params?.framework) queryParams.append('framework', params.framework);
        if (params?.search) queryParams.append('search', params.search);
        if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

        return apiClient.get<PaginatedResponse<AIProject>>(`/ai/projects?${queryParams}`);
    }

    static async getAIProjectTemplates(): Promise<ApiResponse<AIProjectTemplate[]>> {
        if (isDevelopment) {
            await mockDelay();
            return {
                success: true,
                data: mockAIProjectTemplates
            };
        }

        return apiClient.get<AIProjectTemplate[]>('/ai/templates');
    }

    static async getAIProject(projectId: string): Promise<ApiResponse<AIProject>> {
        if (isDevelopment) {
            await mockDelay();
            const project = mockAIProjects.find(p => p.id === projectId);
            if (!project) {
                return {
                    success: false,
                    data: {} as AIProject,
                    message: '项目不存在'
                };
            }
            return {
                success: true,
                data: project
            };
        }

        return apiClient.get<AIProject>(`/ai/projects/${projectId}`);
    }

    static async createAIProject(data: CreateAIProjectParams): Promise<ApiResponse<AIProject>> {
        if (isDevelopment) {
            await mockDelay();
            
            const template = data.templateId ? mockAIProjectTemplates.find(t => t.id === data.templateId) : null;
            
            const newAIProject: AIProject = {
                id: `ai_project_${Date.now()}`,
                name: data.name,
                description: data.description || '',
                type: data.type,
                framework: data.framework,
                status: 'pending',
                progress: 0,
                template: data.templateId,
                config: template ? { ...template.defaultConfig, ...data.config } : data.config,
                training: data.training ? {
                    epochs: data.training.epochs || 10,
                    batchSize: data.training.batchSize || 32,
                    learningRate: data.training.learningRate || 0.001,
                    ...data.training
                } : undefined,
                owner: 'current_user', // 实际应从认证信息获取
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                cost: 0
            };
            
            mockAIProjects.push(newAIProject);
            
            return {
                success: true,
                data: newAIProject,
                message: 'AI项目创建成功'
            };
        }
        return apiClient.post<AIProject>('/ai/projects', data);
    }

    static async updateAIProject(projectId: string, data: UpdateAIProjectParams): Promise<ApiResponse<AIProject>> {
        if (isDevelopment) {
            await mockDelay();
            
            const projectIndex = mockAIProjects.findIndex(p => p.id === projectId);
            if (projectIndex === -1) {
                return {
                    success: false,
                    data: {} as AIProject,
                    message: '项目不存在'
                };
            }
            
            const project = mockAIProjects[projectIndex];
            const updatedProject: AIProject = {
                ...project,
                ...data,
                config: data.config ? { ...project.config, ...data.config } : project.config,
                training: data.training && project.training ? {
                    epochs: data.training.epochs ?? project.training.epochs,
                    batchSize: data.training.batchSize ?? project.training.batchSize,
                    learningRate: data.training.learningRate ?? project.training.learningRate,
                    currentEpoch: data.training.currentEpoch ?? project.training.currentEpoch,
                    loss: data.training.loss ?? project.training.loss,
                    accuracy: data.training.accuracy ?? project.training.accuracy
                } : project.training,
                updatedAt: new Date().toISOString()
            };
            
            mockAIProjects[projectIndex] = updatedProject;
            
            return {
                success: true,
                data: updatedProject,
                message: 'AI项目更新成功'
            };
        }
        return apiClient.put<AIProject>(`/ai/projects/${projectId}`, data);
    }

    static async deleteAIProject(projectId: string): Promise<ApiResponse<void>> {
        if (isDevelopment) {
            await mockDelay();
            
            const projectIndex = mockAIProjects.findIndex(p => p.id === projectId);
            if (projectIndex === -1) {
                return {
                    success: false,
                    data: undefined,
                    message: '项目不存在'
                };
            }
            
            mockAIProjects.splice(projectIndex, 1);
            
            return {
                success: true,
                data: undefined,
                message: 'AI项目删除成功'
            };
        }
        return apiClient.delete<void>(`/ai/projects/${projectId}`);
    }

    static async executeAIProjectAction(projectId: string, action: AIProjectAction): Promise<ApiResponse<AIProjectActionResponse>> {
        if (isDevelopment) {
            await mockDelay();
            
            const projectIndex = mockAIProjects.findIndex(p => p.id === projectId);
            if (projectIndex === -1) {
                return {
                    success: false,
                    data: {
                        success: false,
                        message: '项目不存在'
                    },
                    message: '项目不存在'
                };
            }
            
            const project = mockAIProjects[projectIndex];
            let newStatus = project.status;
            let message = '';
            
            switch (action.action) {
                case 'start':
                    if (project.status === 'pending' || project.status === 'stopped') {
                        newStatus = 'running';
                        message = '项目启动成功';
                    } else {
                        return {
                            success: false,
                            data: { success: false, message: '项目状态不允许启动' },
                            message: '项目状态不允许启动'
                        };
                    }
                    break;
                case 'stop':
                    if (project.status === 'running') {
                        newStatus = 'stopped';
                        message = '项目停止成功';
                    } else {
                        return {
                            success: false,
                            data: { success: false, message: '项目状态不允许停止' },
                            message: '项目状态不允许停止'
                        };
                    }
                    break;
                case 'pause':
                    if (project.status === 'running') {
                        newStatus = 'stopped';
                        message = '项目暂停成功';
                    } else {
                        return {
                            success: false,
                            data: { success: false, message: '项目状态不允许暂停' },
                            message: '项目状态不允许暂停'
                        };
                    }
                    break;
                case 'resume':
                    if (project.status === 'stopped') {
                        newStatus = 'running';
                        message = '项目恢复成功';
                    } else {
                        return {
                            success: false,
                            data: { success: false, message: '项目状态不允许恢复' },
                            message: '项目状态不允许恢复'
                        };
                    }
                    break;
                case 'delete':
                    mockAIProjects.splice(projectIndex, 1);
                    return {
                        success: true,
                        data: { success: true, message: '项目删除成功' },
                        message: '项目删除成功'
                    };
                default:
                    return {
                        success: false,
                        data: { success: false, message: '不支持的操作' },
                        message: '不支持的操作'
                    };
            }
            
            // 更新项目状态
            mockAIProjects[projectIndex] = {
                ...project,
                status: newStatus,
                updatedAt: new Date().toISOString()
            };
            
            return {
                success: true,
                data: {
                    success: true,
                    message,
                    data: { newStatus }
                },
                message
            };
        }
        return apiClient.post<AIProjectActionResponse>(`/ai/projects/${projectId}/actions`, action);
    }
}

export default ApiService;